/**
 * CSV export (PLAN §5): `imdbID,Title,Year,WatchedDate,Rating,Rewatch`,
 * movies only — stated plainly below, since Letterboxd itself is movies-only
 * and a TV season has nothing meaningful to export into that format.
 */

import { useEffect, useRef, useState } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { exportLetterboxdCsv } from "@/lib/letterboxd/export.ts";
import { useProgressStore } from "@/state/progress.ts";
import styles from "./ExportDialog.module.css";

export function ExportDialog() {
  const catalog = useCatalog();
  const watched = useProgressStore((s) => s.watched);
  const records = useProgressStore((s) => s.records);
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  const watchedMovieCount = catalog.entries.filter(
    (e) => e.kind === "movie" && watched[e.id],
  ).length;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const close = () => setOpen(false);

  const download = () => {
    const csv = exportLetterboxdCsv(catalog.entries, records);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "mcu-road-letterboxd-export.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <button type="button" className={styles.trigger} onClick={() => setOpen(true)}>
        Export to Letterboxd
      </button>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: click here is backdrop-dismissal only (native ::backdrop clicks land on the <dialog> itself); a modal <dialog> already closes on Escape natively. */}
      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-label="Export to Letterboxd"
        onClose={close}
        onClick={(event) => {
          if (event.target === dialogRef.current) close();
        }}
      >
        <div className={styles.inner}>
          <div className={styles.head}>
            <h2 className={styles.title}>Export to Letterboxd</h2>
            <button type="button" className={styles.closeButton} onClick={close}>
              Close
            </button>
          </div>
          <p className={styles.note}>
            Movies only. Letterboxd doesn't track TV, so seasons aren't in this file. Import the
            download at Letterboxd's Settings → Import & Export → Import your data;{" "}
            <code>imdbID</code> is the key Letterboxd matches on.
          </p>
          <code className={styles.columns}>imdbID,Title,Year,WatchedDate,Rating,Rewatch</code>
          <p className={styles.count}>
            {watchedMovieCount} watched {watchedMovieCount === 1 ? "movie" : "movies"} ready to
            export.
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.downloadButton}
              disabled={watchedMovieCount === 0}
              onClick={download}
            >
              Download CSV
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
}
